<div id="root">
  <div class="fixed-background">
    <div class="container document">
      <div class="row">
        <div class="col-12 col-md-12">
          <div class="card">
            <app-legal-lang-toggle [lang]="lang" (langChange)="onLangChange($event)"></app-legal-lang-toggle>
            @if (lang === 'en') {
            <h1 class="mt-4" style="text-align: center;">Deleting your BoothPilot Replay account</h1>
            <p><i>Last updated: August 18, 2026</i></p>
            <p>This page explains how to delete your BoothPilot Replay account and what happens to your data when you
              do. It applies to accounts created in the BoothPilot Replay mobile application, published by Laurent
              Chicoine Productions.</p>

            <h2>Deleting from the app</h2>
            <p>The fastest way is from within the Application:</p>
            <ol>
              <li>Open BoothPilot Replay and sign in.</li>
              <li>Open the menu and go to <strong>Account Settings</strong>.</li>
              <li>Choose <strong>Delete Account</strong> and confirm.</li>
            </ol>
            <p>Deletion is immediate and permanent. It cannot be undone.</p>

            <h2>Requesting deletion without the app</h2>
            <p>If you can no longer install or sign in to the Application, email
              <a href="mailto:support&#64;boothpilot.com">support&#64;boothpilot.com</a> from the email address on the
              account, with the subject <strong>Account deletion request</strong>. We will verify that the request
              comes from the account holder and complete the deletion within 30 days. We will confirm by email when it
              is done.</p>

            <h2>If you own a team</h2>
            <p>If you are the owner of a team that still has other members, the Application will ask you to transfer
              ownership or remove the remaining members before your account can be deleted. This protects your
              teammates from losing access to a team that no longer has an owner. If you are the only member of a team
              you own, the team is deleted along with your account.</p>

            <h2>What is deleted</h2>
            <ul>
              <li>Your sign-in credentials and authentication account.</li>
              <li>Your user profile, including your name and email address, in every region we operate.</li>
              <li>Your membership and role in every team you belong to.</li>
              <li>For any team you own as its only member: the team itself, its events, clip metadata, plays,
                outstanding invitations, and its stored Google Drive connection.</li>
            </ul>

            <h2>What is not deleted</h2>
            <ul>
              <li><strong>Video files.</strong> Recordings are stored in your team's own Google Drive account, which
                we do not control. Deleting your account does not remove them. To delete the footage, delete the clips
                in the Application before deleting your account, or ask your team administrator to remove the files
                from the team's Drive.</li>
              <li><strong>Game data belonging to a team you do not own.</strong> Events, plays and tags created for a
                team remain with that team after you leave it, because they belong to the team as a whole rather than
                to you individually.</li>
              <li><strong>Support correspondence.</strong> Support requests and feedback you sent us, and our
                replies, are kept for as long as needed to resolve your request and to keep a record of the issue
                and how it was handled, and are deleted when they are no longer needed for that purpose.</li>
              <li><strong>Records we are required to keep</strong> by applicable law, for as long as the law
                requires.</li>
            </ul>
            <p>Where backups exist, they are retained for up to 365 days. Deleting your account removes your
              data from the live service immediately; any copy still held in a backup is removed as that backup
              ages out of the retention window. We do not restore deleted accounts from backups.</p>

            <h2>Local copies on your device</h2>
            <p>Clips cached on your device for offline playback are removed when you uninstall the Application.</p>

            <h2>Questions</h2>
            <p>Email <a href="mailto:support&#64;boothpilot.com">support&#64;boothpilot.com</a>. Our
              <a routerLink="/privacy">Privacy Policy</a> describes what we collect and why.</p>
            }
            @if (lang === 'fr') {
            <h1 class="mt-4" style="text-align: center;">Suppression de votre compte BoothPilot Replay</h1>
            <p><i>Dernière mise à jour : 18 août 2026</i></p>
            <p>Cette page explique comment supprimer votre compte BoothPilot Replay et ce qu'il advient de vos
              données. Elle vise les comptes créés dans l'application mobile BoothPilot Replay, publiée par Laurent
              Chicoine Productions.</p>

            <h2>Supprimer depuis l'application</h2>
            <p>La façon la plus rapide est de le faire depuis l'Application :</p>
            <ol>
              <li>Ouvrez BoothPilot Replay et connectez-vous.</li>
              <li>Ouvrez le menu et allez à <strong>Paramètres du compte</strong>.</li>
              <li>Choisissez <strong>Supprimer le compte</strong> et confirmez.</li>
            </ol>
            <p>La suppression est immédiate et permanente. Elle est irréversible.</p>

            <h2>Demander la suppression sans l'application</h2>
            <p>Si vous ne pouvez plus installer l'Application ou vous y connecter, écrivez à
              <a href="mailto:support&#64;boothpilot.com">support&#64;boothpilot.com</a> depuis l'adresse courriel
              associée au compte, avec l'objet <strong>Demande de suppression de compte</strong>. Nous vérifierons que
              la demande provient du titulaire du compte et effectuerons la suppression dans les 30 jours. Nous vous
              confirmerons par courriel lorsque ce sera fait.</p>

            <h2>Si vous êtes propriétaire d'une équipe</h2>
            <p>Si vous êtes propriétaire d'une équipe qui compte encore d'autres membres, l'Application vous demandera
              de transférer la propriété ou de retirer les membres restants avant de pouvoir supprimer votre compte.
              Cela évite que vos coéquipiers se retrouvent dans une équipe sans propriétaire. Si vous êtes le seul
              membre d'une équipe dont vous êtes propriétaire, l'équipe est supprimée en même temps que votre
              compte.</p>

            <h2>Ce qui est supprimé</h2>
            <ul>
              <li>Vos identifiants de connexion et votre compte d'authentification.</li>
              <li>Votre profil d'utilisateur, y compris votre nom et votre adresse courriel, dans toutes les régions
                où nous exploitons le Service.</li>
              <li>Votre appartenance et votre rôle dans chacune de vos équipes.</li>
              <li>Pour une équipe dont vous êtes propriétaire et seul membre : l'équipe elle-même, ses événements, les
                métadonnées de séquences, les jeux, les invitations en attente et la connexion Google Drive
                enregistrée.</li>
            </ul>

            <h2>Ce qui n'est pas supprimé</h2>
            <ul>
              <li><strong>Les fichiers vidéo.</strong> Les enregistrements sont stockés dans le compte Google Drive de
                votre équipe, que nous ne contrôlons pas. La suppression de votre compte ne les efface pas. Pour
                supprimer les séquences, supprimez-les dans l'Application avant de supprimer votre compte, ou demandez
                à l'administrateur de votre équipe de les retirer du Drive de l'équipe.</li>
              <li><strong>Les données de jeu d'une équipe dont vous n'êtes pas propriétaire.</strong> Les événements,
                les jeux et les étiquettes créés pour une équipe demeurent avec celle-ci après votre départ, car ils
                appartiennent à l'équipe dans son ensemble plutôt qu'à vous personnellement.</li>
              <li><strong>La correspondance d'assistance.</strong> Les demandes d'assistance et les commentaires que
                vous nous avez envoyés, ainsi que nos réponses, sont conservés aussi longtemps qu'il le faut pour
                traiter votre demande et garder une trace du problème et de son traitement, puis sont supprimés
                lorsqu'ils ne sont plus nécessaires à cette fin.</li>
              <li><strong>Les renseignements que la loi nous oblige à conserver</strong>, pour la durée qu'elle
                exige.</li>
            </ul>
            <p>Lorsque des copies de sauvegarde existent, elles sont conservées pendant au plus 365 jours. La
              suppression de votre compte retire vos données du service actif immédiatement; toute copie encore
              présente dans une sauvegarde est retirée à mesure que cette sauvegarde sort de la période de
              conservation. Nous ne restaurons pas les comptes supprimés à partir des sauvegardes.</p>

            <h2>Copies locales sur votre appareil</h2>
            <p>Les séquences mises en cache sur votre appareil pour la lecture hors ligne sont supprimées lorsque vous
              désinstallez l'Application.</p>

            <h2>Questions</h2>
            <p>Écrivez à <a href="mailto:support&#64;boothpilot.com">support&#64;boothpilot.com</a>. Notre
              <a routerLink="/privacy">politique de confidentialité</a> décrit ce que nous recueillons et
              pourquoi.</p>
            }
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
